/**
 * useSubmitEvent — mutation for contributor Catalyst submissions.
 *
 * POSTs a CatalystCreateRequest JSON body to POST /api/catalysts.
 * Required fields: geofield (WKT), impact_count, event_date, organization_id.
 *
 * On success, invalidates the impactEvents query so the new pin appears on map.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/axios';
import { useUIStore } from '@/stores/uiStore';

/**
 * Convert lat/lng floats to a WKT POINT string.
 * API expects: "POINT (lon lat)"
 */
export function toWkt(lng, lat) {
  return `POINT (${lng} ${lat})`;
}

export function useSubmitEvent() {
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    /**
     * @param {Object} payload  CatalystCreateRequest fields:
     *   geofield, impact_count, event_date, organization_id,
     *   narrative, security_level, event_type, story, lives_affected, region
     */
    mutationFn: async (payload) => {
      const { data } = await apiClient.post('/api/catalysts', payload);
      return data; // { status, uuid, id }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['impactEvents'] });
      addToast({
        title: 'Catalyst submitted',
        description: 'Your report has been submitted and will appear on the map shortly.',
        variant: 'success',
      });
    },
    onError: (err) => {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        'Please try again.';
      addToast({
        title: 'Submission failed',
        description: message,
        variant: 'destructive',
      });
    },
  });
}
